/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Dropdown } from '@blend/enterprise-components';
import { AxiosError } from 'axios';
import React from 'react';
import { useDispatch } from 'react-redux';

import { addApiErrorNotification } from '../../../actions/pageActions';
import { notaryAPI } from '../../../api/notary';
import { VideoProviderName } from '../../../contexts/videoProvider/types';
import { getConfig } from '@utils/configs';

export namespace VideoProviderDropdown {
  export interface Props {
    roomId: string;
    defaultValue: string;
    handleChange: () => void;
    disabled?: boolean;
  }
}

const videoOptions: Dropdown.Option[] = [
  { text: VideoProviderName.ZOOM, value: VideoProviderName.ZOOM, disabled: false },
  { text: VideoProviderName.TWILIO, value: VideoProviderName.TWILIO, disabled: false },
];

export const VideoProviderDropdown: React.FC<VideoProviderDropdown.Props> = ({
  roomId,
  defaultValue,
  handleChange,
  disabled,
}) => {
  const dispatch = useDispatch();

  const onVideoProviderChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    try {
      const provider = e.target.value;
      await notaryAPI.rooms.setVideoProviderOverride(roomId, provider);
    } catch (err) {
      addApiErrorNotification('Error changing video provider', err as AxiosError<{ message?: string }>)(dispatch);
    }
    handleChange();
  };

  const defaultProvider = getConfig()?.enableZoomForBSR20240730 ? VideoProviderName.ZOOM : VideoProviderName.TWILIO;
  const defaultVideoProviderName = defaultValue || defaultProvider;

  return (
    <div>
      <Dropdown
        options={videoOptions}
        valueRequired={true}
        defaultValue={defaultVideoProviderName}
        onChange={onVideoProviderChange}
        data-it="VideoProviderDropdown-dropdown"
        disabled={!!disabled}
      />
    </div>
  );
};
